/**
 * Composant racine — Routing principal de l'application Arketyp
 */

import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { MainLayout } from './components/layout/MainLayout.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { LoginPage } from './pages/LoginPage.js';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage.js';
import { ResetPasswordPage } from './pages/ResetPasswordPage.js';
import { PublicAnalysePage } from './pages/PublicAnalysePage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { CoursesPage } from './pages/CoursesPage.js';
import { ClientsPage } from './pages/ClientsPage.js';
import { ClientDetailPage } from './pages/ClientDetailPage.js';
import { CreateClientPage } from './pages/CreateClientPage.js';
import { EditClientPage } from './pages/EditClientPage.js';
import { ImportPage } from './pages/ImportPage.js';
import { ClientLayout } from './components/clients/ClientLayout.js';
import { ToolsPage } from './pages/ToolsPage.js';
import { SearchPage } from './pages/SearchPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { SetupTOTPPage } from './pages/SetupTOTPPage.js';
import { GeneratorPage } from './pages/GeneratorPage.js';
import { SuperpositionRSPage } from './pages/SuperpositionRSPage.js';
import { TransitsPage } from './pages/TransitsPage.js';
import { MaintenantPage } from './pages/MaintenantPage.js';
import { SeancePage } from './pages/SeancePage.js';
import { TimelinePage } from './pages/TimelinePage.js';
import { CalculsPage } from './pages/CalculsPage.js';
import { AnalyseIAPage } from './pages/AnalyseIAPage.js';
import { AnalyseTransitIAPage } from './pages/AnalyseTransitIAPage.js';
import { NotFoundPage } from './pages/NotFoundPage.js';

/** Pages lazy-loaded (TipTap / arbre) */
const NodePage = lazy(() => import('./pages/NodePage.js'));
const EditFichePage = lazy(() => import('./pages/EditFichePage.js'));
const CreateFichePage = lazy(() => import('./pages/CreateFichePage.js'));
const ManageCoursesPage = lazy(() => import('./pages/ManageCoursesPage.js'));

const LazyFallback = <div style={{ padding: 32, textAlign: 'center', color: '#9A9188' }}>Chargement...</div>;

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Routes publiques */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/p/:token" element={<PublicAnalysePage />} />

        {/* Routes protegees avec layout */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Cours — grille des noeuds racines */}
          <Route path="/courses" element={<CoursesPage />} />

          {/* Redirections anciennes URL */}
          <Route path="/courses/:themeId" element={<RedirectTheme />} />
          <Route path="/courses/fiches/:ficheId" element={<RedirectFiche />} />
          <Route path="/courses/fiches/:ficheId/edit" element={<RedirectFicheEdit />} />

          {/* Cours — creation (ADMIN) */}
          <Route
            path="/cours/nouveau"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <ErrorBoundary>
                  <Suspense fallback={LazyFallback}>
                    <CreateFichePage />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Cours — gestion arborescente (ADMIN) */}
          <Route
            path="/cours/gestion"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <ErrorBoundary>
                  <Suspense fallback={LazyFallback}>
                    <ManageCoursesPage />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Cours — affichage d'un noeud (unifie theme/categorie/fiche) */}
          <Route
            path="/cours/:nodeId"
            element={
              <ErrorBoundary>
                <Suspense fallback={LazyFallback}>
                  <NodePage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          {/* Cours — edition d'un noeud (ADMIN) */}
          <Route
            path="/cours/:ficheId/modifier"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <ErrorBoundary>
                  <Suspense fallback={LazyFallback}>
                    <EditFichePage />
                  </Suspense>
                </ErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Clients */}
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/nouveau" element={<CreateClientPage />} />
          <Route path="/clients/:id/modifier" element={<EditClientPage />} />

          {/* Pages client avec sidebar persistante */}
          <Route path="/clients/:id" element={<ClientLayout />}>
            <Route index element={<ClientDetailPage />} />
            <Route path="import" element={<ImportPage />} />
            <Route path="rs/:chartId" element={<SuperpositionRSPage />} />
            <Route path="prog/:chartId" element={<SuperpositionRSPage />} />
            <Route path="transits/:chartId" element={<TransitsPage />} />
            <Route path="maintenant" element={<MaintenantPage />} />
            <Route path="seances/:seanceId" element={<SeancePage />} />
            <Route path="timeline" element={<TimelinePage />} />
            <Route path="analyse-ia" element={<AnalyseIAPage />} />
            <Route path="analyse-transit-ia/:chartId" element={<AnalyseTransitIAPage />} />
          </Route>

          {/* Outils */}
          <Route path="/tools" element={<ToolsPage />} />

          {/* Generateur de documents */}
          <Route path="/generator" element={<GeneratorPage />} />

          {/* Recherche */}
          <Route path="/search" element={<SearchPage />} />

          {/* Securite — activation 2FA */}
          <Route path="/2fa/setup" element={<SetupTOTPPage />} />

          {/* Configuration — admin seulement */}
          <Route
            path="/settings"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/calculs"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <CalculsPage />
              </ProtectedRoute>
            }
          />

          {/* 404 dans le layout */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Redirections */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

/** Redirections des anciennes URL */
function RedirectTheme() {
  const { themeId } = useParams();
  return <Navigate to={`/cours/${themeId}`} replace />;
}

function RedirectFiche() {
  const { ficheId } = useParams();
  return <Navigate to={`/cours/${ficheId}`} replace />;
}

function RedirectFicheEdit() {
  const { ficheId } = useParams();
  return <Navigate to={`/cours/${ficheId}/modifier`} replace />;
}
